import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Sparkles, TrendingUp, Calendar, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import ScoreCircle from "@/components/ScoreCircle";
import AnimatedBackground from "@/components/AnimatedBackground";
import CustomCursor from "@/components/CustomCursor";

const SUBSCORE_LABELS = {
    clarity: "Clarity",
    credibility: "Credibility",
    consistency: "Consistency",
    attractiveness: "Attractiveness",
    visibility: "Visibility",
    professionalism: "Professionalism",
};

function colorFor(v) {
    if (v <= 4) return "#ef4444";
    if (v <= 7) return "#f97316";
    return "#22c55e";
}

export default function PublicReport() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/public/reports/${token}`);
                setData(data);
            } catch {
                setError("Report not found or share link revoked.");
            } finally {
                setLoading(false);
            }
        })();
    }, [token]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-aura-bg">
                <div className="cyber-spinner" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-aura-bg relative">
                <AnimatedBackground />
                <div className="glass-strong p-10 text-center max-w-md relative z-10">
                    <h1 className="font-syne text-2xl font-bold text-aura-text mb-2">{error || "Not found"}</h1>
                    <Link to="/" className="text-aura-violet hover:underline text-sm font-medium">
                        ← Back to home
                    </Link>
                </div>
            </div>
        );
    }

    const out = data.output || {};
    const subscores = out.subscores || {};

    return (
        <div className="min-h-screen relative bg-aura-bg" data-testid="public-report-page">
            <AnimatedBackground />
            <CustomCursor />

            {/* Top bar */}
            <header className="relative z-10 flex items-center justify-between px-6 lg:px-12 py-5">
                <Link to="/" className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-aura-violet to-aura-cyan flex items-center justify-center glow-violet-soft">
                        <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
                    </div>
                    <span className="font-syne font-bold text-base text-aura-text">MirrorDigital</span>
                </Link>
                <Link
                    to="/register"
                    className="text-sm px-4 py-2 rounded-lg bg-gradient-to-r from-aura-violet to-aura-glow text-white font-dm-sans font-medium glow-violet-soft btn-ripple flex items-center gap-2"
                    data-testid="public-cta-btn"
                >
                    Compute mine <ArrowRight className="w-3.5 h-3.5" />
                </Link>
            </header>

            <main className="relative z-10 max-w-5xl mx-auto px-6 pb-20">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                    <div className="font-jetbrains-mono text-xs tracking-[0.3em] text-aura-violet mb-2">
                        // PUBLIC REPORT · SHARED BY {data.sharedBy?.toUpperCase()}
                    </div>
                    <h1 className="font-syne text-4xl sm:text-5xl font-bold text-aura-text mb-8">
                        {data.sharedBy}'s <span className="text-grad-violet">aura</span>
                    </h1>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="glass p-6 lg:p-10 grid lg:grid-cols-[280px_1fr] gap-8 items-center mb-6">
                    <ScoreCircle score={data.score} size={240} />
                    <div>
                        <div className="font-jetbrains-mono text-[10px] uppercase tracking-[0.3em] text-aura-violet mb-2">
                            // SYSTEM PERCEPTION
                        </div>
                        <p className="font-syne text-xl lg:text-2xl text-aura-text leading-snug">{out.perception}</p>
                        <div className="mt-5 p-4 bg-aura-elevate/80 border border-aura-violet/10 rounded-lg">
                            <div className="font-jetbrains-mono text-[10px] uppercase tracking-wider text-aura-muted mb-1">
                                Optimized identity
                            </div>
                            <div className="font-dm-sans text-sm text-aura-text">{out.optimized_identity}</div>
                        </div>
                    </div>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass p-6 lg:p-8 mb-6">
                    <h2 className="font-syne text-2xl font-semibold text-aura-text mb-5">Score Breakdown</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
                        {Object.entries(SUBSCORE_LABELS).map(([k, label]) => {
                            const v = subscores[k] ?? 0;
                            const color = colorFor(v);
                            return (
                                <div key={k}>
                                    <div className="flex justify-between text-sm mb-1.5">
                                        <span className="font-dm-sans text-aura-text">{label}</span>
                                        <span className="font-jetbrains-mono font-semibold" style={{ color }}>
                                            {v.toFixed(1)}
                                        </span>
                                    </div>
                                    <div className="h-2 bg-aura-elevate rounded-full overflow-hidden">
                                        <div
                                            className="h-full rounded-full"
                                            style={{ width: `${(v / 10) * 100}%`, background: color, boxShadow: `0 0 12px ${color}66` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }} className="glass p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <TrendingUp className="w-4 h-4 text-aura-cyan" />
                            <h3 className="font-syne text-lg font-semibold text-aura-text">Strengths</h3>
                        </div>
                        <ul className="space-y-3">
                            {(out.strengths || []).map((s, i) => (
                                <li key={i} className="flex gap-3 text-sm">
                                    <span className="font-jetbrains-mono text-aura-cyan text-xs mt-0.5 font-semibold">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-aura-text">{s}</span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.18 }} className="glass p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <ArrowRight className="w-4 h-4 text-orange-500 rotate-180" />
                            <h3 className="font-syne text-lg font-semibold text-aura-text">Friction Points</h3>
                        </div>
                        <ul className="space-y-3">
                            {(out.weaknesses || []).map((s, i) => (
                                <li key={i} className="flex gap-3 text-sm">
                                    <span className="font-jetbrains-mono text-orange-500 text-xs mt-0.5 font-semibold">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-aura-text">{s}</span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                </div>

                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.22 }} className="glass p-6 lg:p-8 mb-6">
                    <div className="flex items-center gap-2 mb-5">
                        <Calendar className="w-4 h-4 text-aura-violet" />
                        <h3 className="font-syne text-2xl font-semibold text-aura-text">7-Day Action Plan</h3>
                    </div>
                    <div className="space-y-3">
                        {(out.action_plan_7_days || []).map((d) => (
                            <div key={d.day} className="flex gap-4 p-4 rounded-lg bg-white/70 border border-aura-violet/10">
                                <div className="w-12 h-12 shrink-0 rounded-lg bg-gradient-to-br from-aura-violet/15 to-aura-cyan/15 border border-aura-violet/30 flex flex-col items-center justify-center">
                                    <span className="font-jetbrains-mono text-[9px] tracking-wider text-aura-violet">DAY</span>
                                    <span className="font-syne text-lg font-bold leading-none text-aura-text">{d.day}</span>
                                </div>
                                <div className="flex-1">
                                    <div className="font-dm-sans font-medium text-aura-text">{d.task}</div>
                                    <div className="text-xs text-aura-body mt-1">{d.why}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.26 }} className="glass p-6 lg:p-8 border-l-4 border-aura-cyan mb-10">
                    <div className="font-jetbrains-mono text-[10px] uppercase tracking-[0.3em] text-aura-cyan mb-2">
                        // GROWTH STRATEGY
                    </div>
                    <p className="font-syne text-lg leading-relaxed text-aura-text">{out.growth_strategy}</p>
                </motion.div>

                {/* CTA */}
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 }} className="glass-strong p-8 text-center">
                    <Sparkles className="w-7 h-7 text-aura-violet mx-auto mb-4" />
                    <h2 className="font-syne text-3xl font-bold text-aura-text mb-3">
                        Want your own <span className="text-grad-violet">AURA</span>?
                    </h2>
                    <p className="text-aura-body mb-5 max-w-md mx-auto">
                        Free tier — no card required. Decode your signal in 60 seconds.
                    </p>
                    <Link
                        to="/register"
                        className="inline-flex items-center gap-2 px-7 py-3 rounded-xl bg-gradient-to-r from-aura-violet via-aura-glow to-aura-cyan text-white font-dm-sans font-medium glow-violet btn-ripple"
                    >
                        Compute my AURA <ArrowRight className="w-4 h-4" />
                    </Link>
                </motion.div>
            </main>
        </div>
    );
}

